// PEME KPI statistics - avg clearance time and overdue crew cards
import React from 'react';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import { cn } from 'lib/utils';
import './peme-kpi-stats.scss';

const SkeletonCard = () => (
  <div className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-p-5 tw-animate-pulse">
    <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-h-3 tw-w-36 tw-bg-slate-200 tw-rounded" />
        <div className="tw-h-3 tw-w-48 tw-bg-slate-200 tw-rounded" />
      </div>
      <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-slate-200 tw-flex-shrink-0" />
    </div>
    <div className="tw-h-9 tw-w-24 tw-bg-slate-200 tw-rounded tw-mt-2" />
    <div className="tw-flex tw-gap-4 tw-mt-3">
      <div className="tw-h-3 tw-w-16 tw-bg-slate-200 tw-rounded" />
      <div className="tw-h-3 tw-w-16 tw-bg-slate-200 tw-rounded" />
      <div className="tw-h-3 tw-w-16 tw-bg-slate-200 tw-rounded" />
    </div>
  </div>
);

const PemeKpiStats = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  const overdue = stats?.overdueCrewCount ?? 0;
  const isOverdue = overdue > 0;

  return (
    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-4">
      {/* Card 1: Avg. Time to Clearance */}
      <div
        className="tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-p-5 tw-border-l-[3px] tw-border-l-indigo-400"
      >
        <div className="tw-flex tw-items-start tw-justify-between">
          <div>
            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-m-0">
              Avg. Time to Clearance
            </p>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">
              First appointment &rarr; Fit to Work
            </p>
          </div>
          <div
            className="tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-bg-indigo-50"
            aria-hidden="true"
          >
            <TrendingUp className="tw-w-4 tw-h-4 tw-text-indigo-500" />
          </div>
        </div>

        <p className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mt-3 tw-mb-0 tw-leading-none">
          {stats?.avgClearanceDays ?? '—'} <span className="tw-text-lg tw-font-medium tw-text-gray-500">days</span>
        </p>

        {/* Sub-stats row */}
        <div className="tw-flex tw-items-center tw-gap-5 tw-mt-3">
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-font-semibold">
              Fastest
            </span>
            <span className="tw-text-sm tw-font-bold tw-text-emerald-600">
              {stats?.fastestClearanceDays ?? '—'}d
            </span>
          </div>
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-font-semibold">
              Slowest
            </span>
            <span className="tw-text-sm tw-font-bold tw-text-rose-500">
              {stats?.slowestClearanceDays ?? '—'}d
            </span>
          </div>
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-gray-400 tw-font-semibold">
              Sample
            </span>
            <span className="tw-text-sm tw-font-bold tw-text-gray-500">
              {stats?.sampleSize ?? '—'} crew
            </span>
          </div>
        </div>
      </div>

      {/* Card 2: Overdue Crew */}
      <div
        className={cn(
          'tw-bg-white tw-rounded-xl tw-p-5 tw-border',
          isOverdue ? 'tw-border-rose-200' : 'tw-border-slate-200'
        )}
      >
        <div className="tw-flex tw-items-center tw-gap-3">
          <div
            className={cn(
              'tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0',
              isOverdue ? 'tw-bg-rose-50' : 'tw-bg-slate-100'
            )}
            aria-hidden="true"
          >
            <AlertTriangle
              className={cn(
                'tw-w-4 tw-h-4',
                isOverdue ? 'tw-text-rose-500' : 'tw-text-slate-400'
              )}
            />
          </div>
          <div>
            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-m-0">
              Overdue Crew
            </p>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">
              Stuck at a single stage for 10+ days
            </p>
          </div>
        </div>

        <p className="tw-mt-3 tw-mb-0 tw-leading-none">
          <span
            className={cn(
              'tw-text-3xl tw-font-bold',
              isOverdue ? 'tw-text-rose-600' : 'tw-text-gray-900'
            )}
          >
            {overdue}
          </span>
          <span className="tw-text-lg tw-font-medium tw-text-gray-500 tw-ml-1">crew</span>
        </p>
      </div>
    </div>
  );
};

export default PemeKpiStats;
